/**
 * ComparisonTable component for side-by-side creative comparison
 * Shows creatives as columns and metrics as rows
 */

import { Play, Clock, ExternalLink, Image } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import type { PercentileInfo } from '@/components/analysis';
import { Card } from '@/components/ui/Card';
import type { ComparisonData } from '@/hooks/useComparison';
import { decisionColors, colors } from '@/lib/design-tokens';
import { cn } from '@/lib/utils';
import type { MediaType } from '@/types';

import { ComparisonRow } from './ComparisonRow';

// ============================================================================
// TYPES
// ============================================================================

export interface ComparisonTableProps {
  items: ComparisonData[];
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Percentile helpers (from PerformanceContextCard)
function getPercentileColor(percentile: number, higherIsBetter: boolean | null): string {
  if (higherIsBetter === null) return '#9CA3AF';
  const effectivePercentile = higherIsBetter ? percentile : 100 - percentile;
  if (effectivePercentile >= 75) return colors.status.success;
  if (effectivePercentile >= 50) return colors.status.warning;
  if (effectivePercentile >= 25) return '#F97316';
  return colors.status.danger;
}

function getPercentileLabel(percentile: number, higherIsBetter: boolean | null): string {
  if (higherIsBetter === null) return `${percentile}th pctl`;
  const effectivePercentile = higherIsBetter ? percentile : 100 - percentile;
  if (effectivePercentile >= 75) return 'Top Performer';
  if (effectivePercentile >= 50) return 'Above Average';
  if (effectivePercentile >= 25) return 'Below Average';
  return 'Needs Improvement';
}

// Inline percentile bar component
function PercentileBar({ info }: { info: PercentileInfo }) {
  const color = getPercentileColor(info.percentile, info.higherIsBetter);
  const label = getPercentileLabel(info.percentile, info.higherIsBetter);

  return (
    <div className="space-y-0.5 mt-1">
      <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{ width: `${info.percentile}%`, backgroundColor: color }}
        />
      </div>
      <div className="flex justify-between text-[10px]">
        <span style={{ color }}>{label}</span>
        <span className="text-muted">{info.percentile}th</span>
      </div>
    </div>
  );
}

// ============================================================================
// CREATIVE HEADER COMPONENT
// ============================================================================

interface CreativeHeaderProps {
  item: ComparisonData;
}

/** Get effective media type for a creative (defaults to 'video' for backward compat) */
function getCreativeMediaType(creative: ComparisonData['creative']): MediaType {
  if (creative.mediaType) return creative.mediaType;
  const imageFormats = ['jpg', 'jpeg', 'png', 'webp'];
  if (imageFormats.includes(creative.format?.toLowerCase() || '')) return 'image';
  return 'video';
}

function CreativeHeader({ item }: CreativeHeaderProps) {
  const { creative } = item;
  const decision = item.analysis?.result?.decision.call;
  const isImage = getCreativeMediaType(creative) === 'image';

  return (
    <div className="flex flex-col items-center gap-3">
      {/* Thumbnail */}
      <div className="relative w-full aspect-video bg-surface-subtle rounded-lg overflow-hidden group">
        {creative.thumbnailUrl ? (
          <img
            src={creative.thumbnailUrl}
            alt={creative.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-surface-subtle to-border-subtle">
            {isImage ? (
              <Image className="h-8 w-8 text-muted" />
            ) : (
              <Play className="h-8 w-8 text-muted" />
            )}
          </div>
        )}

        {/* Duration / IMG badge */}
        <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-0.5 rounded text-xs font-medium">
          {isImage ? (
            <span className="inline-flex items-center gap-1">
              <Image className="h-3 w-3" />
              IMG
            </span>
          ) : (
            formatDuration(creative.duration)
          )}
        </div>

        {/* Decision badge */}
        {decision && (
          <div
            className="absolute top-2 left-2 px-2 py-1 rounded text-xs font-bold uppercase"
            style={{
              backgroundColor: decisionColors[decision].bg,
              color: decisionColors[decision].text,
            }}
          >
            {decision}
          </div>
        )}
      </div>

      {/* Creative name with link */}
      <Link
        to={`/creatives/${creative.creativeId}`}
        className="group flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-brand transition-colors"
      >
        <span className="line-clamp-1">{creative.name}</span>
        <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
      </Link>

      {/* Metadata */}
      <div className="flex items-center gap-2 text-xs text-muted">
        <Clock className="h-3 w-3" />
        <span>{formatDate(creative.createdAt)}</span>
      </div>

      {/* Tags */}
      {creative.metadata && (
        <div className="flex flex-wrap justify-center gap-1.5">
          {creative.metadata.objective && (
            <span className="px-2 py-0.5 rounded text-xs bg-surface-subtle text-secondary">
              {creative.metadata.objective}
            </span>
          )}
          {creative.metadata.placement && (
            <span className="px-2 py-0.5 rounded text-xs bg-surface-subtle text-secondary">
              {creative.metadata.placement}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// EXPANDED CONTENT RENDERERS
// ============================================================================

function renderSecondaryReasons(item: ComparisonData): React.ReactNode {
  const reasons = item.analysis?.result?.decision.secondaryReasons;
  if (!reasons || reasons.length === 0) {
    return <span className="text-muted text-xs">No secondary reasons</span>;
  }

  return (
    <ul className="space-y-1.5 text-left">
      {reasons.map((reason, index) => (
        <li key={index} className="flex items-start gap-2 text-xs text-secondary">
          <span className="text-muted mt-0.5">•</span>
          <span className="leading-relaxed">{reason}</span>
        </li>
      ))}
    </ul>
  );
}

function renderQualityDetails(item: ComparisonData): React.ReactNode {
  const qualityGate = item.analysis?.result?.qualityGate;
  if (!qualityGate) {
    return <span className="text-muted text-xs">No quality data</span>;
  }

  const checks = Object.entries(qualityGate).map(([key, check]) => ({
    key,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    label: (check as Record<string, unknown>)?.name as string ?? key,
    check: check as { status: string },
  }));

  return (
    <div className="grid grid-cols-2 gap-2 text-xs">
      {checks.map(({ key, label, check }) => {
        if (!check) return null;
        return (
          <div
            key={key}
            className={cn(
              'flex items-center gap-1.5 px-2 py-1 rounded',
              check.status === 'PASS'
                ? 'bg-success-soft text-success'
                : 'bg-danger-soft text-danger'
            )}
          >
            {check.status === 'PASS' ? (
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            )}
            <span className="font-medium">{label}</span>
          </div>
        );
      })}
    </div>
  );
}

function renderEditsList(item: ComparisonData): React.ReactNode {
  const edits = item.analysis?.result?.edits;
  if (!edits || edits.length === 0) {
    return <span className="text-muted text-xs">No edit suggestions</span>;
  }

  return (
    <ul className="space-y-2 text-left">
      {edits.slice(0, 3).map((edit, index) => (
        <li key={index} className="text-xs">
          <div className="flex items-start gap-2">
            <span
              className={cn(
                'flex-shrink-0 px-1.5 py-0.5 rounded text-xs font-bold uppercase',
                edit.effort === 'S' && 'bg-success-soft text-success',
                edit.effort === 'M' && 'bg-warning-soft text-warning',
                edit.effort === 'L' && 'bg-danger-soft text-danger'
              )}
            >
              {edit.effort}
            </span>
            <span className="text-secondary line-clamp-2">{edit.instruction}</span>
          </div>
        </li>
      ))}
      {edits.length > 3 && (
        <li className="text-xs text-muted">
          +{edits.length - 3} more edit{edits.length - 3 !== 1 ? 's' : ''}
        </li>
      )}
    </ul>
  );
}

// ============================================================================
// PERFORMANCE METRICS EXPANDED RENDERERS
// ============================================================================

function formatCurrency(value: number | undefined): string {
  if (value === undefined) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatNumber(value: number | undefined): string {
  if (value === undefined) return '-';
  return new Intl.NumberFormat('en-US').format(value);
}

function formatPercentage(value: number | undefined, decimals: number = 1): string {
  if (value === undefined) return '-';
  return `${value.toFixed(decimals)}%`;
}

function renderCostDetails(item: ComparisonData): React.ReactNode {
  const metrics = item.creative.performanceMetrics;
  const percentiles = item.performanceContext?.percentiles;

  if (!metrics) {
    return <span className="text-muted text-xs">No performance metrics</span>;
  }

  const rows = [
    { label: 'Spend', value: formatCurrency(metrics.spend), percentile: percentiles?.spend },
    { label: 'Impressions', value: formatNumber(metrics.impressions), percentile: percentiles?.impressions },
    { label: 'CPM', value: formatCurrency(metrics.cpm), percentile: percentiles?.cpm },
  ];

  return (
    <div className="space-y-3 text-left">
      {rows.map(({ label, value, percentile }) => (
        <div key={label}>
          <div className="flex justify-between items-center text-xs">
            <span className="text-muted">{label}</span>
            <span className="font-medium text-primary">{value}</span>
          </div>
          {percentile && <PercentileBar info={percentile} />}
        </div>
      ))}
    </div>
  );
}

function renderViewRateDetails(item: ComparisonData): React.ReactNode {
  const metrics = item.creative.performanceMetrics;
  const percentiles = item.performanceContext?.percentiles;

  if (!metrics) {
    return <span className="text-muted text-xs">No performance metrics</span>;
  }

  const rows = [
    { label: '2s View Rate', value: formatPercentage(metrics.viewRate2s), percentile: percentiles?.viewRate2s },
    { label: '3s View Rate', value: formatPercentage(metrics.viewRate3s), percentile: percentiles?.viewRate3s },
    { label: 'ThruPlay Rate', value: formatPercentage(metrics.thruPlayRate), percentile: percentiles?.thruPlayRate },
  ];

  return (
    <div className="space-y-3 text-left">
      {rows.map(({ label, value, percentile }) => (
        <div key={label}>
          <div className="flex justify-between items-center text-xs">
            <span className="text-muted">{label}</span>
            <span className="font-medium text-primary">{value}</span>
          </div>
          {percentile && <PercentileBar info={percentile} />}
        </div>
      ))}
    </div>
  );
}

function renderEngagementDetails(item: ComparisonData): React.ReactNode {
  const metrics = item.creative.performanceMetrics;
  const percentiles = item.performanceContext?.percentiles;

  if (!metrics) {
    return <span className="text-muted text-xs">No performance metrics</span>;
  }

  const rows = [
    { label: 'CTR', value: formatPercentage(metrics.ctr, 2), percentile: percentiles?.ctr },
    { label: 'CPC', value: formatCurrency(metrics.cpc), percentile: percentiles?.cpc },
  ];

  return (
    <div className="space-y-3 text-left">
      {rows.map(({ label, value, percentile }) => (
        <div key={label}>
          <div className="flex justify-between items-center text-xs">
            <span className="text-muted">{label}</span>
            <span className="font-medium text-primary">{value}</span>
          </div>
          {percentile && <PercentileBar info={percentile} />}
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// COMPARISON TABLE COMPONENT
// ============================================================================

export function ComparisonTable({ items }: ComparisonTableProps) {
  // Calculate column width based on number of items
  const columnWidth = items.length <= 3 ? 'w-64' : 'w-52';

  // Determine comparison media type from first creative (all same type due to same-type enforcement)
  const comparisonMediaType: MediaType = items.length > 0
    ? getCreativeMediaType(items[0]!.creative)
    : 'video';

  return (
    <Card className="overflow-hidden p-0">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          {/* Header with creative thumbnails */}
          <thead>
            <tr className="bg-surface-subtle border-b border-border-subtle">
              <th className="p-4 text-left text-xs font-semibold uppercase tracking-wider text-muted sticky left-0 bg-surface-subtle w-40">
                Metric
              </th>
              {items.map((item) => (
                <th
                  key={item.creative.creativeId}
                  className={cn('p-4', columnWidth)}
                >
                  <CreativeHeader item={item} />
                </th>
              ))}
            </tr>
          </thead>

          {/* Body with comparison rows */}
          <tbody className="bg-surface">
            {/* Decision Section */}
            <tr className="bg-surface-subtle">
              <td
                colSpan={items.length + 1}
                className="py-2 px-4 text-xs font-bold uppercase tracking-wider text-muted"
              >
                Decision
              </td>
            </tr>
            <ComparisonRow
              label="Verdict"
              metric="decision"
              items={items}
            />
            <ComparisonRow
              label="Confidence"
              metric="confidence"
              items={items}
            />
            <ComparisonRow
              label="Primary Reason"
              metric="primaryReason"
              items={items}
              isExpandable
              renderExpanded={renderSecondaryReasons}
            />

            {/* Brand Metrics Section - branches by media type */}
            <tr className="bg-surface-subtle">
              <td
                colSpan={items.length + 1}
                className="py-2 px-4 text-xs font-bold uppercase tracking-wider text-muted"
              >
                Brand Metrics
              </td>
            </tr>
            {comparisonMediaType === 'image' ? (
              <>
                <ComparisonRow
                  label="Logo Prominence"
                  metric="logoProminence"
                  items={items}
                />
                <ComparisonRow
                  label="Product Prominence"
                  metric="productProminence"
                  items={items}
                />
                <ComparisonRow
                  label="Brand at Glance"
                  metric="brandIdentifiable"
                  items={items}
                />
              </>
            ) : (
              <>
                <ComparisonRow
                  label="Logo Duration"
                  metric="logoDuration"
                  items={items}
                />
                <ComparisonRow
                  label="Product Duration"
                  metric="productDuration"
                  items={items}
                />
                <ComparisonRow
                  label="Brand Cue by 2s"
                  metric="brandCueBy2s"
                  items={items}
                />
              </>
            )}

            {/* Quality Section */}
            <tr className="bg-surface-subtle">
              <td
                colSpan={items.length + 1}
                className="py-2 px-4 text-xs font-bold uppercase tracking-wider text-muted"
              >
                Quality & Edits
              </td>
            </tr>
            <ComparisonRow
              label="Quality Gate"
              metric="qualityGate"
              items={items}
              isExpandable
              renderExpanded={renderQualityDetails}
            />
            <ComparisonRow
              label="Suggested Edits"
              metric="editsCount"
              items={items}
              isExpandable
              renderExpanded={renderEditsList}
            />

            {/* Performance Metrics Section */}
            <tr className="bg-surface-subtle">
              <td
                colSpan={items.length + 1}
                className="py-2 px-4 text-xs font-bold uppercase tracking-wider text-muted"
              >
                Performance Metrics
              </td>
            </tr>
            <ComparisonRow
              label="Cost & Reach"
              metric="spend"
              items={items}
              isExpandable
              renderExpanded={renderCostDetails}
            />
            <ComparisonRow
              label="View Rates"
              metric="viewRate2s"
              items={items}
              isExpandable
              renderExpanded={renderViewRateDetails}
            />
            <ComparisonRow
              label="Engagement"
              metric="ctr"
              items={items}
              isExpandable
              renderExpanded={renderEngagementDetails}
            />
          </tbody>
        </table>
      </div>
    </Card>
  );
}
